import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { ISearchDecorationOptions, ISearchOptions, SearchAddon } from "@xterm/addon-search";
import { ArrowDownIcon, ArrowUpIcon, CaseSensitiveIcon, RegexIcon, WholeWordIcon, XIcon } from "lucide-react";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useEventListener } from "usehooks-ts";
import { HoverTip } from '@/components/ui/tooltip';


type ButtonProps = {
    title?: string;
    onClick: () => void;
    isActive?: boolean;
    children: ReactNode;
};

function SearchBarButton({ title, onClick, isActive, children }: ButtonProps) {
    return (
        <HoverTip label={title}>
            <button
                aria-label={title}
                className={cn(
                    'pressable size-7 shrink-0 rounded-bar flex items-center justify-center',
                    'border border-transparent text-faint transition-colors',
                    'hover:border-edge hover:bg-tile hover:text-fg',
                    'focus:outline-none focus-visible:border-accent/45 focus-visible:text-fg',
                    isActive && 'border-accent/45 bg-accent/10 text-accent hover:border-accent/70 hover:bg-accent/20 hover:text-accent'
                )}
                onClick={onClick}
            >
                {children}
            </button>
        </HoverTip>
    );
}


const labelNoResults = 'No results';
const xtermDecorations = {
    activeMatchBackground: '#a70978',
    activeMatchColorOverviewRuler: '#f42ab8',
    matchBackground: '#450431',
    matchOverviewRuler: '#9e0871',
} satisfies ISearchDecorationOptions;

type LiveConsoleSearchBarProps = {
    show: boolean;
    setShow: (show: boolean) => void;
    searchAddon: SearchAddon;
};

export default function LiveConsoleSearchBar({ show, setShow, searchAddon }: LiveConsoleSearchBarProps) {
    const [caseSensitive, setCaseSensitive] = useState(false);
    const [wholeWord, setWholeWord] = useState(false);
    const [regex, setRegex] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [resultCount, setResultCount] = useState(labelNoResults);

    //helpers
    const clearSearchState = (newStatus?: string) => {
        searchAddon.clearDecorations();
        if (newStatus) {
            setResultCount(newStatus);
        }
    }
    const getSearchOptions = (overrides?: Partial<ISearchOptions>): ISearchOptions => ({
        decorations: xtermDecorations,
        caseSensitive,
        wholeWord,
        regex,
        ...overrides,
    })

    //autofocus the input
    useEffect(() => {
        if (show) {
            inputRef.current?.focus();
        } else {
            clearSearchState(labelNoResults);
        }
    }, [show]);

    //listens to the result count change
    useEffect(() => {
        if (!searchAddon) return;
        const dispose = searchAddon.onDidChangeResults(({ resultIndex, resultCount }) => {
            if (resultIndex === -1) {
                setResultCount(labelNoResults);
            } else {
                setResultCount(`${resultIndex + 1}/${resultCount}`);
            }
        });
        return () => {
            dispose.dispose();
        }
    }, []);

    //Handlers
    const handlePrevious = () => {
        if (!inputRef.current || !inputRef.current.value) return;
        searchAddon.findPrevious(inputRef.current.value, getSearchOptions());
    }
    const handleNext = () => {
        if (!inputRef.current || !inputRef.current.value) return;
        searchAddon.findNext(inputRef.current.value, getSearchOptions());
    }
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!inputRef.current) return;
        if (e.code === 'Enter') {
            if (e.shiftKey) {
                handlePrevious();
            } else {
                handleNext();
            }
            e.preventDefault();
        }
    }
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!inputRef.current) return;
        handleNext();
    }

    const handleCaseSensitiveMode = () => {
        if (!inputRef.current) return;
        setCaseSensitive(!caseSensitive);
        clearSearchState();
        searchAddon.findNext(inputRef.current.value, getSearchOptions({ caseSensitive: !caseSensitive }));
    }
    const handleWholeWordMode = () => {
        if (!inputRef.current) return;
        setWholeWord(!wholeWord);
        clearSearchState();
        searchAddon.findNext(inputRef.current.value, getSearchOptions({ wholeWord: !wholeWord }));
    }
    const handleRegexMode = () => {
        if (!inputRef.current) return;
        setRegex(!regex);
        clearSearchState();
        searchAddon.findNext(inputRef.current.value, getSearchOptions({ regex: !regex }));
    }

    //This is required so hotkeys in the page also apply in here
    useEventListener('message', (e: TxMessageEvent) => {
        if (e.data.type !== 'liveConsoleSearchHotkey') return;
        if (e.data.action === 'previous') {
            handlePrevious();
        } else if (e.data.action === 'next') {
            handleNext();
        } else if (e.data.action === 'focus') {
            inputRef.current?.focus();
        }
    });

    if (!show) return null;
    return (
        <div className={cn(
            'absolute top-0 xs:right-4 z-10 w-full xs:w-auto flex flex-wrap items-center justify-center',
            'gap-2 xs:gap-3 px-3 py-2 rounded-b-hud border border-t-0 border-edge bg-popover shadow-pop',
        )}>
            <div className="relative">
                <Input
                    ref={inputRef}
                    className="h-9 pr-[6.75rem]"
                    placeholder="Search string"
                    onKeyDown={handleInputKeyDown}
                    onChange={handleInputChange}
                    onBlur={() => { searchAddon.clearActiveDecoration() }}
                />
                <div className="absolute top-1/2 right-1 transform -translate-y-1/2 flex gap-1">
                    <SearchBarButton
                        title="Case Sensitive"
                        isActive={caseSensitive}
                        onClick={handleCaseSensitiveMode}
                    >
                        <CaseSensitiveIcon className="h-5 w-5" />
                    </SearchBarButton>
                    <SearchBarButton
                        title="Whole Word"
                        isActive={wholeWord}
                        onClick={handleWholeWordMode}
                    >
                        <WholeWordIcon className="h-5 w-5" />
                    </SearchBarButton>
                    <SearchBarButton
                        title="Regex"
                        isActive={regex}
                        onClick={handleRegexMode}
                    >
                        <RegexIcon className="h-4 w-5" />
                    </SearchBarButton>
                </div>
            </div>
            <div className="flex grow font-rad text-caption text-faint whitespace-nowrap min-w-[8ch] scrim">
                {resultCount}
            </div>
            <div className="flex gap-1">
                <SearchBarButton
                    title="Previous"
                    onClick={handlePrevious}
                >
                    <ArrowUpIcon className="h-5 w-5" />
                </SearchBarButton>
                <SearchBarButton
                    title="Next"
                    onClick={handleNext}
                >
                    <ArrowDownIcon className="h-5 w-5" />
                </SearchBarButton>
                <SearchBarButton
                    title="Close"
                    onClick={() => { setShow(false) }}
                >
                    <XIcon className="h-5 w-5" />
                </SearchBarButton>
            </div>
        </div>
    );
}
